import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { CheckCircle2, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = 'https://images.unsplash.com/photo-1694880912546-5d9619757fbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxsYXR2aWElMjBsYW5kc2NhcGUlMjBmb2d8ZW58MHx8fHwxNzg0MTcwOTc1fDA&ixlib=rb-4.1.0&q=85';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { checkAuth } = useAuth();
  const token = params.get('token');
  const [state, setState] = useState('verifying');
  const [error, setError] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);

  useEffect(() => {
    if (!token) { setState('error'); setError(tr('Trūkst saites tokens', 'Missing token', 'Отсутствует токен ссылки')); return; }
    axios.post(`${API}/auth/verify-email`, { token })
      .then(async () => {
        setState('success');
        // The endpoint sets auth cookies, so refresh user state to log in.
        try { await checkAuth(); } catch {}
        setTimeout(() => navigate('/welcome'), 2000);
      })
      .catch((e) => {
        setState('error');
        setError(formatApiError(e, tr('Saites pārbaude neizdevās', 'Verification failed', 'Не удалось подтвердить')));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendStatus('sending');
    try { await axios.post(`${API}/auth/resend-verification`, { email: resendEmail.trim().toLowerCase() }); } catch {}
    setResendStatus('sent');
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto py-10 px-5" style={{ background: '#0A0D0B' }}>
      <div className="absolute inset-0 z-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-30 scale-105" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 8%, rgba(10,13,11,0.15), rgba(10,13,11,0.9))' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md rounded-[28px] border border-white/10 bg-[#0A0D0B]/70 backdrop-blur-2xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] p-8 sm:p-10 text-center"
        style={{ fontFamily: 'Manrope, sans-serif' }}
        data-testid="verify-email-page"
      >
        {state === 'verifying' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-spin" />
            <h1 className="text-white text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{tr('Pārbaudām...', 'Verifying...', 'Проверяем...')}</h1>
            <p className="text-white/60 text-sm">{tr('Apstiprinām tavu e-pastu', 'Confirming your email', 'Подтверждаем вашу почту')}</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-500/15 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-green-400" />
            </div>
            <h1 className="text-white text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{tr('E-pasts apstiprināts!', 'Email verified!', 'Почта подтверждена!')}</h1>
            <p className="text-white/60 text-sm mb-7">{tr('Tavs konts ir aktīvs. Turpinām ar iestatīšanu...', 'Your account is active. Continuing setup...', 'Ваш аккаунт активен. Продолжаем настройку...')}</p>
            <Link to="/welcome">
              <button className="w-full h-12 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors" style={{ boxShadow: '0 8px 30px rgba(255,159,28,0.35)' }}>
                {tr('Turpināt', 'Continue', 'Продолжить')} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-500/15 flex items-center justify-center">
              <XCircle className="w-9 h-9 text-red-400" />
            </div>
            <h1 className="text-white text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{tr('Neizdevās apstiprināt', 'Verification failed', 'Не удалось подтвердить')}</h1>
            <p className="text-white/60 text-sm mb-6">{error}</p>

            <div className="border-t border-white/10 pt-5 mt-5 text-left">
              <p className="text-sm text-white/60 mb-3">{tr('Pieprasīt jaunu saiti:', 'Request a new link:', 'Запросить новую ссылку:')}</p>
              {resendStatus === 'sent' ? (
                <div className="rounded-2xl bg-green-500/10 border border-green-500/30 text-green-300 text-sm p-3 flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" /> {tr('Pārbaudi iesūtni!', 'Check your inbox!', 'Проверьте почту!')}
                </div>
              ) : (
                <form onSubmit={handleResend} className="flex gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <input
                      type="email"
                      placeholder={tr('Tavs e-pasts', 'Your email', 'Ваша почта')}
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      required
                      className="w-full h-11 pl-11 pr-4 rounded-2xl bg-white/5 border border-white/10 text-white placeholder-white/40 text-sm focus:border-amber-500/60 focus:outline-none transition-colors"
                      data-testid="resend-email-input"
                    />
                  </div>
                  <button type="submit" disabled={resendStatus === 'sending'} className="h-11 px-5 rounded-full bg-amber-500 text-[#0A0D0B] font-bold text-sm hover:bg-amber-400 transition-colors disabled:opacity-60 shrink-0" data-testid="resend-verification-btn">
                    {resendStatus === 'sending' ? tr('Sūta...', 'Sending...', 'Отправка...') : tr('Nosūtīt', 'Send', 'Отправить')}
                  </button>
                </form>
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
